import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export const useEditorUnsavedModal = ({ changes }) => {
  const [showModal, setShowModal] = useState(false)
  const [nextLocation, setNextLocation] = useState(null)
  const location = useLocation()
  const navigate = useNavigate()

  // The history patch must read these at call time, not through the effect
  // closure — legacy read order.unsavedChanges live off the MobX model, which
  // is what let a confirmed navigation pass back through the patch.
  const changesRef = useRef(changes)
  changesRef.current = changes
  const bypassRef = useRef(false)

  // Handle browser beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (changes) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    if (changes) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [changes])

  // Custom navigation blocking logic
  useEffect(() => {
    if (!changes) return

    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    const interceptNavigation = (url) => {
      if (bypassRef.current) return true
      if (changesRef.current && url !== location.pathname) {
        setNextLocation(url)
        setShowModal(true)
        return false
      }
      return true
    }

    window.history.pushState = function (state, title, url) {
      if (interceptNavigation(url)) {
        originalPushState.call(this, state, title, url)
      }
    }

    window.history.replaceState = function (state, title, url) {
      if (interceptNavigation(url)) {
        originalReplaceState.call(this, state, title, url)
      }
    }

    return () => {
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [changes, location.pathname])

  const handleConfirm = () => {
    setShowModal(false)
    if (nextLocation) {
      bypassRef.current = true
      try {
        navigate(nextLocation)
      } finally {
        bypassRef.current = false
      }
      setNextLocation(null)
    }
  }

  const handleCancel = () => {
    setShowModal(false)
    setNextLocation(null)
  }

  return {
    showModal,
    handleConfirm,
    handleCancel,
  }
}
